import { TopoMap } from "../Topology/TopoMap";
import { Point, Rectangle, Matrix } from "../Utils/Matrix";

export class MapRenderer {

	private DefaultZoomMiddle = 100;
	private DefaultZoomMax = 200;
	private DefaultZoomMin = 0;
	private DefaultZoomChange = 5;
	private DragScrollStep = 30;
	private DefaultScale = 1.0;

	private _canvas: HTMLCanvasElement;
	private _workingMap: TopoMap;
	private _scale: number;

	public ZoomMiddle: number;
	public ZoomMax: number;
	public ZoomMin: number;
	public ZoomChange: number;

	constructor() {
		this.ZoomMiddle = this.DefaultZoomMiddle;
		this.ZoomMax = this.DefaultZoomMax;
		this.ZoomMin = this.DefaultZoomMin;
		this.ZoomChange = this.DefaultZoomChange;

		this._scale = this.DefaultScale;
		this._zoomValue = this.DefaultZoomMiddle;
	}

	private _zoomValue: number;
	public get ZoomValue(): number {
		return this._zoomValue;
	}
	public set ZoomValue(value: number) {
		this._zoomValue = value;
		if (this._zoomValue > this.ZoomMax)
			this._zoomValue = this.ZoomMax;
		else if (this._zoomValue < this.ZoomMin)
			this._zoomValue = this.ZoomMin;

		var center = this._ViewCenter;
		this.ZoomAtLocation(center.X, center.Y, this._zoomValue);
	}

	public SetCanvas(canvas: HTMLCanvasElement) {
		this._canvas = canvas;

		window.onwheel = (event: WheelEvent) => {
				
			var x = event.clientX;
			var y = event.clientY;

			var delta = event.deltaY;
			if (delta < 0) {
				this.ZoomInAt(x, y);
			} else {
				this.ZoomOutAt(x, y);
			}
				
			return false;
		}
	}

	public ZoomOutAt(x: number, y: number) {
		var newZoom = this.ZoomValue - this.ZoomChange;

		if (newZoom < this.ZoomMin)
			newZoom = this.ZoomMin;

		this.ZoomAtLocation(x, y, newZoom);
	}

	public ZoomInAt(x: number, y: number) {
		var newZoom = this.ZoomValue + this.ZoomChange;
		if (newZoom > this.ZoomMax)
			newZoom = this.ZoomMax;

		this.ZoomAtLocation(x, y, newZoom);
	}

	public SetMap(map: TopoMap): void {
		this._workingMap = map;
	}

	public Draw(): void {
		var canvas = this._canvas;
		var map = this._workingMap;
		if (canvas) {
			canvas.width = this.DisplayWidth;
			canvas.height = this.DisplayHeight;
			var ctx: CanvasRenderingContext2D = this._canvas.getContext('2d');
			ctx.clearRect(0, 0, canvas.width, canvas.height);

			ctx.translate(this.ViewWidth, this.ViewHeight);
			ctx.translate(-map.Left, -map.Top);
			ctx.scale(this._scale, this._scale);
								
			this._workingMap.TopoEdges.ForEach((edge) => {
				var lx = edge.LocalNode.X + (edge.LocalNode.Width / 2);
				var ly = edge.LocalNode.Y + (edge.LocalNode.Height / 2);
				var rx = edge.RemoteNode.X + (edge.RemoteNode.Width / 2);
				var ry = edge.RemoteNode.Y + (edge.RemoteNode.Height / 2);

				ctx.beginPath();
				ctx.moveTo(lx, ly);
				ctx.lineTo(rx, ry);
				ctx.setLineDash([2, 5, 4, 6]);
				ctx.stroke();
			});

			this._workingMap.VisibleNodes.ForEach((node) => {
				ctx.drawImage(node.Image, node.X, node.Y);
				this.DrawLabel(node.CenterX, node.Bottom + 14, node.DisplayName, ctx);
			});
		}
	}

	public DrawLabel(x:number, y:number, label: string, ctx: CanvasRenderingContext2D): void {
		var mets = ctx.measureText(label);

		x -= mets.width / 2;
		y -= 5;
		ctx.fillText(label, x, y);
	}

	private get DisplayWidth(): number {
		if (this._workingMap != null)
			return Math.ceil((this._workingMap.Width * this._scale) + (this.ViewWidth * 2));
		else
			return this.ViewWidth * 2;
	}

	private get DisplayHeight(): number {
		if (this._workingMap != null)
			return Math.ceil((this._workingMap.Height * this._scale) + (this.ViewHeight * 2));
		else
			return this.ViewHeight * 2;
	}

	private get ViewWidth(): number {
		return this._canvas.parentElement.clientWidth;
	}

	private get ViewHeight(): number {
		return this._canvas.parentElement.clientHeight;
	}

	public ScrollAndZoomToCenter(): void {
		this.ZoomToFit();
		this._ScrollToCenter();
	}

	private _ScrollToCenter(): void {
		this.ScrollLeft = this._canvas.scrollWidth / 2;;
		this.ScrollTop = this._canvas.scrollHeight / 2;
	}

	private set ScrollLeft(value: number) {
		this._canvas.parentElement.scrollLeft = value - (this.ViewWidth/2);
	}

	private set ScrollTop(value: number) {
		this._canvas.parentElement.scrollTop = value - (this.ViewHeight/2);
	}

	private ZoomToFit(): void {
		var viewWidth = this.ViewWidth;
		var viewHeight = this.ViewHeight;

		this._workingMap.CalculateSize();
		var mapWidth = this._workingMap.Width;
		var mapHeight = this._workingMap.Height;

		if (mapWidth > 0 && mapHeight > 0) {
			var widthRatio = (viewWidth / mapWidth);
			var heightRatio = (viewHeight / mapHeight);

			var ratio = widthRatio > heightRatio ? heightRatio : widthRatio;

			var zoomValue = (this.ZoomMiddle * ratio);
			if (zoomValue > this.ZoomMiddle)
				zoomValue = this.ZoomMiddle;

			zoomValue = Math.round(zoomValue/this.ZoomChange) * this.ZoomChange;

			this.ZoomValue = zoomValue;
		}
	}

		

	private get _ViewCenter(): Point {
		return new Point(this.ViewWidth / 2, this.ViewHeight / 2);
	}

	private Zoom(zoomValue:number): void {
		var scale = zoomValue / this.ZoomMiddle;

		if (scale <= 0)
			scale = 0.01;

		if (scale > 0) {
			this._scale = scale;
			this._SetAutoScrollMinSize();
			this._zoomValue = zoomValue;
		}
	}

	private ZoomAtLocation(x: number, y: number, zoomValue:number) {
		var zoomPoint = new Point(x, y);

		// Cache world point before Zooming
		var worldPoint = this.ViewToWorld(zoomPoint);

		// Zoom
		this.Zoom(zoomValue);

		// Scroll Back to location
		this.ScrollWorldPointToViewPoint(worldPoint, zoomPoint);
	}


	private ScrollWorldPointToViewPoint(worldPoint: Point, viewPoint: Point): void {
		var newView: Point = this.WorldToView(worldPoint);

		var xOffset = viewPoint.X - newView.X;
		var yOffset = viewPoint.Y - newView.Y;

		this.ScrollOffset(xOffset, yOffset);
	}

	private ScrollOffset(horizOffset:number, vertOffset:number): void {
		this.ScrollTop = this.ScrollTop - horizOffset;
		this.ScrollLeft = this.ScrollLeft - vertOffset;
	}


	public CreateInverseTransformMatrix(): Matrix {
		var matrix = new Matrix();

		matrix.Scale(1/this._scale, 1/this._scale);
		matrix.Translate(this._workingMap.Left, this._workingMap.Top);
		matrix.Translate(-this.ViewWidth, -this.ViewHeight);

		return matrix;
	}

	public CreateTransformMatrix(): Matrix {
		var matrix = new Matrix();

		matrix.Translate(this.ViewWidth, this.ViewHeight);
		matrix.Translate(-this._workingMap.Left, -this._workingMap.Top);
		matrix.Scale(this._scale, this._scale);

		return matrix;
	}

	public ViewToWorld(point: Point): Point {
		var tranformArray: Point[]  =[ new Point(point.X, point.Y) ];

		var inverseMatrix: Matrix = this.CreateInverseTransformMatrix();
			
		inverseMatrix.TransformPoints(tranformArray);

		return tranformArray[0];
	}

	public WorldToView(point: Point): Point {
		var result: Point[] = [ new Point(point.X, point.Y) ];

		this.CreateTransformMatrix().TransformPoints(result);

		return result[0];
	}
		
	private _SetAutoScrollMinSize(): void {
		//this._canvas.width = this.DisplayWidth;
		//this._canvas.height = this.DisplayHeight;
		//this.AutoScrollMinSize = new Size(DisplayWidth, DisplayHeight);
	}
}
